/**
 * «کجا»: یک کادرِ متنی، و اگر خواستی یک نقطه روی نقشه — قرینه‌ی
 * apps/mobile/src/features/discover/LocationPicker.tsx.
 *
 * ⚠ این **اولین نقشه‌ی سایت** است. تا امروز «نقشه» جزوِ استثناهای ثبت‌شده‌ی
 *   وب بود (CLAUDE.md: «no map yet») چون هیچ کتابخانه‌ی نقشه‌ای انتخاب نشده
 *   بود. اینجا هم چیزی انتخاب نشد: همان صفحه‌ی Leafletی که موبایل داخلِ
 *   WebView نشان می‌دهد، اینجا داخلِ یک `<iframe srcdoc>` می‌نشیند. یعنی صفر
 *   وابستگیِ تازه در `package.json`، و دقیقاً همان نقشه در هر دو جا.
 *   نقشه‌ی **فهرستِ کشف** همچنان روی وب نیست؛ این فقط انتخابِ یک نقطه است.
 *
 * نقشه تا زده باز نمی‌شود: کسی که فقط می‌خواهد آدرس تایپ کند نباید منتظرِ
 * بار شدنِ کاشی‌های نقشه بماند.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { mapPickerHtml, usablePoint, type PickedPoint } from '@/core/lib/mapPicker';
import { Help } from '@/ui/kit';

export function LocationPicker({ address, onAddress, point, onPoint, center }: {
  address: string;
  onAddress: (v: string) => void;
  point: PickedPoint | null;
  onPoint: (p: PickedPoint | null) => void;
  center?: PickedPoint | null;
}) {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);

  /**
   * صفحه یک‌بار ساخته می‌شود — اگر به `point` وابسته بود، هر کلیک روی نقشه
   * کلِ iframe را از نو بار می‌کرد و نقشه به جای اولش می‌پرید.
   */
  const html = useMemo(
    () => mapPickerHtml(point, center ?? undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );

  /**
   * پیامِ داخلِ iframe.
   *
   * فقط پیامی که واقعاً از همین قاب آمده خوانده می‌شود (`e.source`): صفحه
   * ممکن است iframeهای دیگری هم داشته باشد و `message` روی کلِ پنجره می‌آید.
   */
  useEffect(() => {
    if (!open) return undefined;
    const onMessage = (e: MessageEvent) => {
      if (frame.current && e.source !== frame.current.contentWindow) return;
      try {
        const m = JSON.parse(String(e.data)) as {
          type: string; lat?: number; lng?: number; message?: string;
        };
        if (m.type === 'pick' && usablePoint(m as PickedPoint)) {
          onPoint({ lat: m.lat as number, lng: m.lng as number });
        } else if (m.type === 'error') {
          setFailed(true);
          reportError('web.mapPicker', m.message ?? 'js error');
        }
      } catch { /* پیامِ کسِ دیگر */ }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [open, onPoint]);

  return (
    <div className="field">
      <span>
        {t('fieldWhere')}
        <Help text={t('whereHint')} />
      </span>
      <input className="input" value={address} placeholder={t('suggestAddressPh')}
        onChange={(e) => onAddress(e.target.value)} />

      <div className="chiprow" style={{ marginTop: 6 }}>
        <button type="button" className="btn" onClick={() => setOpen((v) => !v)}>
          {t('pickOnMap')}
        </button>
        {usablePoint(point) ? (
          <>
            <span className="count ok">{t('mapPicked')}</span>
            <button type="button" className="btn" onClick={() => onPoint(null)}>
              {t('mapClear')}
            </button>
          </>
        ) : null}
      </div>

      {open ? (
        <>
          <p className="dim">{t('mapPickHint')}</p>
          {/* `sandbox` عمداً محدود است: اسکریپت لازم دارد (خودِ Leaflet)، ولی
              نه دسترسی به همان مبدأ. یعنی این قاب به نشستِ کاربر نمی‌رسد. */}
          <iframe ref={frame} className="map-frame" title={t('pickOnMap')}
            srcDoc={html} sandbox="allow-scripts"
            onError={() => setFailed(true)} />
          {failed ? <p className="dim">{t('mapPickFailed')}</p> : null}
        </>
      ) : null}
    </div>
  );
}
